import {
  invalidateSessionHrDataAnalysisCache,
  repairSessionHrDataWithAi,
} from '../../../ai-repair';
import { FETCH_CONCURRENCY } from '../constants';

export type BulkRepairSessionResult = {
  sessionId: number;
  status: 'success' | 'no-op' | 'failed';
  message?: string;
};

export type BulkRepairSummary = {
  results: BulkRepairSessionResult[];
  successCount: number;
  failedCount: number;
  noOpCount: number;
};

type RepairSessionsInParallelOptions = {
  concurrency?: number;
  onProgress?: (completed: number, total: number) => void;
};

const toBulkResult = (
  sessionId: number,
  result: Awaited<ReturnType<typeof repairSessionHrDataWithAi>>
): BulkRepairSessionResult => {
  if (result.status === 'success' && result.repairedEvents) {
    return { sessionId, status: 'success' };
  }

  if (result.status === 'no-op') {
    return {
      sessionId,
      status: 'no-op',
      message: result.message,
    };
  }

  return {
    sessionId,
    status: 'failed',
    message: result.message ?? 'Riparazione non riuscita.',
  };
};

export const repairSessionsInParallel = async (
  sessionIds: number[],
  options: RepairSessionsInParallelOptions = {}
): Promise<BulkRepairSummary> => {
  const concurrency = options.concurrency ?? FETCH_CONCURRENCY;
  const total = sessionIds.length;
  const results: BulkRepairSessionResult[] = new Array(total);
  let completed = 0;
  let nextIndex = 0;

  const reportProgress = () => {
    options.onProgress?.(completed, total);
  };

  const worker = async () => {
    while (true) {
      const currentIndex = nextIndex;
      nextIndex += 1;

      if (currentIndex >= total) {
        return;
      }

      const sessionId = sessionIds[currentIndex];

      try {
        const result = await repairSessionHrDataWithAi(sessionId);
        const bulkResult = toBulkResult(sessionId, result);
        results[currentIndex] = bulkResult;

        if (bulkResult.status === 'success') {
          invalidateSessionHrDataAnalysisCache(sessionId);
        }
      } catch (error) {
        results[currentIndex] = {
          sessionId,
          status: 'failed',
          message:
            error instanceof Error
              ? error.message
              : 'Riparazione non riuscita.',
        };
      } finally {
        completed += 1;
        reportProgress();
      }
    }
  };

  if (total === 0) {
    reportProgress();
    return {
      results: [],
      successCount: 0,
      failedCount: 0,
      noOpCount: 0,
    };
  }

  const workerCount = Math.min(concurrency, total);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  const successCount = results.filter(
    result => result.status === 'success'
  ).length;
  const failedCount = results.filter(
    result => result.status === 'failed'
  ).length;
  const noOpCount = results.filter(result => result.status === 'no-op').length;

  return {
    results,
    successCount,
    failedCount,
    noOpCount,
  };
};
